import { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import * as giveawayApis from 'apis/giveaways';
import './GiveawayDetailsStatic.scss';

/*
  Giveaway Details layout, converted from the giveaway.html mockup.
  Pluggable, self-contained component:
  - All CSS is scoped under ".gd-page" with "gd-" prefixed classes,
    so it can be dropped into the public site or the Dashboard
    without colliding with existing global styles.
  - Renders real giveaway data from the API. Empty fields/sections
    are omitted (no static content fallbacks).
*/

/* Sample assets used while developing this page (not used at runtime). */
// eslint-disable-next-line no-unused-vars
const SAMPLE_GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1571646036117-8015cc02547c',
  'https://images.unsplash.com/photo-1627407811811-56b02ac2493f',
  'https://images.unsplash.com/photo-1755585190999-2c8b62c03a49',
  'https://images.unsplash.com/photo-1761000989410-3fa81f1b94cb',
];

// eslint-disable-next-line no-unused-vars
const SAMPLE_SPECS = [
  { icon: 'fa-solid fa-gears', value: '998 cc', label: 'Engine' },
  { icon: 'fa-solid fa-bolt', value: '215 hp', label: 'Power' },
  { icon: 'fa-solid fa-diagram-project', value: '6-Speed', label: 'Gearbox' },
  { icon: 'fa-solid fa-fire', value: '113 Nm', label: 'Torque' },
  { icon: 'fa-solid fa-feather', value: '192 kg', label: 'Weight' },
  { icon: 'fa-solid fa-gas-pump', value: '16.5 L', label: 'Fuel Capacity' },
  { icon: 'fa-solid fa-gauge-high', value: '306 km/h', label: 'Top Speed' },
  { icon: 'fa-solid fa-rocket', value: '3.1 sec', label: '0-100 km/h' },
];

// eslint-disable-next-line no-unused-vars
const SAMPLE_PRIZES = [
  { img: 'https://images.unsplash.com/photo-1627407811811-56b02ac2493f', label: 'BMW M1000RR Competition' },
  { img: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85', label: '1 Year Registration' },
  { img: 'https://images.unsplash.com/photo-1771574207619-ebf770550c16', label: 'Premium Racing Helmet' },
  { img: 'https://images.unsplash.com/photo-1658204157484-34d648049c2d', label: 'Premium Accessories' },
  { img: 'https://images.unsplash.com/photo-1580674285999-8ea89504687f', label: '₹2,00,000 Cash Prize' },
];

// eslint-disable-next-line no-unused-vars
const SAMPLE_FEATURES = [
  'Carbon Winglets',
  'Launch Control',
  'Dynamic Traction Control',
  'Riding Modes Pro',
  'Quick Shifter Pro',
  'TFT Display',
  'Titanium Sport Exhaust',
  'Race ABS Pro',
];

// eslint-disable-next-line no-unused-vars
const SAMPLE_RELATED_GIVEAWAYS = [
  { img: 'https://images.unsplash.com/photo-1771071890455-f681dcfb8708', title: 'Porsche 911 Turbo S', status: 'live' },
  { img: 'https://images.unsplash.com/photo-1753563823155-64164a43c830', title: 'Ducati Panigale V4', status: 'live' },
  { img: 'https://images.unsplash.com/photo-1709620435533-56483034bdd4', title: 'Toyota Land Cruiser 300', status: 'live' },
  { img: 'https://images.unsplash.com/photo-1618091171668-e1c3cf1ffce2', title: 'Mercedes AMG G63', status: 'upcoming' },
];

const TERMS = [
  {
    icon: 'fa-solid fa-user',
    title: 'Eligibility',
    body: 'Open to all participants aged 18 and above with a valid government ID. Employees of Taurian and their immediate families are not eligible to participate.',
  },
  {
    icon: 'fa-solid fa-hand-pointer',
    title: 'How to Enter',
    body: 'Pay the entry fee of ₹199 to receive a unique entry number. You may purchase multiple entries to increase your winning chances before the last entry date.',
  },
  {
    icon: 'fa-solid fa-trophy',
    title: 'Winner Selection',
    body: 'The winner is selected through a random, verifiable live draw broadcast on our official channels immediately after entries close.',
  },
  {
    icon: 'fa-solid fa-file-lines',
    title: 'General Conditions',
    body: 'All decisions made by Taurian regarding the draw and winner selection are final. Entry fees are non-refundable once submitted.',
  },
  {
    icon: 'fa-solid fa-truck-fast',
    title: 'Delivery',
    body: "The prize will be delivered to the winner's registered address within 30 working days along with all documentation and warranty cards.",
  },
];

const getTimeLeft = (targetTime) => {
  const diff = Math.max(targetTime - Date.now(), 0);
  return {
    days: String(Math.floor(diff / (1000 * 60 * 60 * 24))).padStart(2, '0'),
    hours: String(Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))).padStart(2, '0'),
    mins: String(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0'),
    secs: String(Math.floor((diff % (1000 * 60)) / 1000)).padStart(2, '0'),
  };
};

const formatDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })} IST`;
};

// Dummy data uses Unsplash URLs that need crop/quality params appended;
// real uploaded assets are already final URLs and should be used as-is.
const withImgParams = (url, params) => (url && url.includes('unsplash.com') ? `${url}?${params}` : url);

const GiveawayDetailsStatic = () => {
  const { id } = useParams();
  const location = useLocation();
  const assetUrl = process.env.REACT_APP_TAURIAN_ASSET_URL;

  const [giveaway, setGiveaway] = useState(null);
  const [relatedGiveaways, setRelatedGiveaways] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [openTerm, setOpenTerm] = useState(0);

  useEffect(() => {
    if (!id) return;
    setGiveaway(null);
    setActiveImage(0);

    const fetchData = async () => {
      try {
        const response = await giveawayApis.fetchGiveaway(id);
        setGiveaway(response.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();

    const fetchRelated = async () => {
      try {
        const response = await giveawayApis.fetchGiveaways();
        setRelatedGiveaways((response.data || []).filter((g) => String(g.id) !== String(id)).slice(0, 4));
      } catch (error) {
        console.log(error);
      }
    };
    fetchRelated();
  }, [id]);

  const brandName = giveaway?.brandName;
  const firstName = giveaway?.firstName;
  const lastName = giveaway?.lastName;
  const shortDescription = giveaway?.shortDescription;
  const description = giveaway?.description;

  const galleryImages = giveaway?.images?.length
    ? giveaway.images.map((img) => `${assetUrl}${img}`)
    : [];

  const specs = giveaway?.attributes?.length
    ? giveaway.attributes.map((attr) => ({ icon: attr.logo, value: attr.value, label: attr.text }))
    : [];

  const prizes = giveaway?.includes?.length
    ? giveaway.includes.map((inc) => ({ img: inc.image ? `${assetUrl}${inc.image}` : '', label: inc.text }))
    : [];

  const features = giveaway?.descriptionList?.length ? giveaway.descriptionList : [];

  const lastEntryDate = giveaway?.endDate ? new Date(giveaway.endDate) : null;
  const drawDate = giveaway?.drawDate ? new Date(giveaway.drawDate) : null;
  const targetTime = lastEntryDate ? lastEntryDate.getTime() : null;

  const [timeLeft, setTimeLeft] = useState(() => (targetTime ? getTimeLeft(targetTime) : null));
  useEffect(() => {
    if (!targetTime) {
      setTimeLeft(null);
      return undefined;
    }
    setTimeLeft(getTimeLeft(targetTime));
    const interval = setInterval(() => setTimeLeft(getTimeLeft(targetTime)), 1000);
    return () => clearInterval(interval);
  }, [targetTime]);

  const relatedItems = (relatedGiveaways || []).map((g) => ({
    id: g.id,
    img: g.images?.[0] ? `${assetUrl}${g.images[0]}` : '',
    title: [g.brandName, g.firstName, g.lastName].filter(Boolean).join(' '),
    status: g.isActive ? 'live' : 'upcoming',
  }));

  const isDashboard = location.pathname.startsWith('/dashboard');
  const relatedHref = (relatedId) => (isDashboard ? `/dashboard/giveaway/${relatedId}` : `/Giveaway/${relatedId}`);

  const token = localStorage.getItem('token');
  const enterHref = token ? '/dashboard' : '/Login';

  return (
    <div className="gd-page">
      <div className="gd-container">

        {/* ===== HERO ===== */}
        <div className="gd-hero-wrap">

          <div className="gd-gallery">
            {galleryImages.length > 0 && (
              <div className="gd-main-image">
                <span className="gd-live-tag"><i className="fa-solid fa-circle"></i> LIVE GIVEAWAY <i className="fa-solid fa-circle"></i></span>
                <div className="gd-gallery-nav gd-prev"><i className="fa-solid fa-chevron-left"></i></div>
                <div className="gd-gallery-nav gd-next"><i className="fa-solid fa-chevron-right"></i></div>
                <img src={withImgParams(galleryImages[activeImage], 'auto=format&fit=crop&w=1400&q=80')} alt={firstName || ''} />
              </div>
            )}

            {galleryImages.length > 0 && (
              <div className="gd-thumbnail-row">
                {galleryImages.map((src, index) => (
                  <img
                    key={src}
                    className={index === activeImage ? 'gd-active' : ''}
                    onClick={() => setActiveImage(index)}
                    src={withImgParams(src, 'auto=format&fit=crop&w=400&q=70')}
                    alt={`Thumbnail ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="gd-info-panel">

            <span className="gd-badge"><i className="fa-solid fa-circle"></i> LIVE GIVEAWAY <i className="fa-solid fa-circle"></i></span>

            {(brandName || firstName || lastName) && (
              <div className="gd-title-block">
                {brandName && <span className="gd-brand">{brandName}</span>}
                {firstName && <span className="gd-model">{firstName}</span>}
                {lastName && <span className="gd-type">{lastName}</span>}
              </div>
            )}

            {shortDescription && <p className="gd-desc">{shortDescription}</p>}

            {timeLeft && (
              <div className="gd-countdown-box">
                <div className="gd-label">DRAW ENDS IN</div>
                <div className="gd-countdown-grid">
                  <div className="gd-unit"><h3>{timeLeft.days}</h3><p>DAYS</p></div>
                  <div className="gd-unit"><h3>{timeLeft.hours}</h3><p>HRS</p></div>
                  <div className="gd-unit"><h3>{timeLeft.mins}</h3><p>MINS</p></div>
                  <div className="gd-unit"><h3>{timeLeft.secs}</h3><p>SECS</p></div>
                </div>
              </div>
            )}

            {(lastEntryDate || drawDate) && (
              <div>
                {lastEntryDate && (
                  <div className="gd-date-row">
                    <i className="fa-solid fa-calendar-days"></i>
                    <span className="gd-d-label">Last Entry Date</span>
                    <span className="gd-d-value">{formatDate(lastEntryDate)}</span>
                  </div>
                )}
                {drawDate && (
                  <div className="gd-date-row">
                    <i className="fa-solid fa-calendar-check"></i>
                    <span className="gd-d-label">Draw Date</span>
                    <span className="gd-d-value">{formatDate(drawDate)}</span>
                  </div>
                )}
              </div>
            )}

            <Link to={enterHref} className="gd-enter-btn">ENTER NOW <i className="fa-solid fa-arrow-right"></i></Link>

            <div className="gd-secure-text"><i className="fa-solid fa-shield-halved"></i> Secure, Transparent, Fair.</div>

          </div>

        </div>

        {/* ===== SPECIFICATIONS ===== */}

        {specs.length > 0 && (
          <>
            <div className="gd-section-header">
              <i className="fa-solid fa-bars"></i>
              <h2>SPECIFICATIONS</h2>
              <div className="gd-spacer"></div>
            </div>

            <div className="gd-spec-grid">
              {specs.map((spec, index) => (
                <div className="gd-spec" key={`${spec.label}-${index}`}>
                  <i className={spec.icon}></i>
                  <h3>{spec.value}</h3>
                  <p>{spec.label}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ===== PRIZE INCLUDES ===== */}

        {prizes.length > 0 && (
          <>
            <div className="gd-section-header">
              <i className="fa-solid fa-bars"></i>
              <h2>THIS GIVEAWAY INCLUDES</h2>
              <div className="gd-spacer"></div>
            </div>

            <div className="gd-prize-grid">
              {prizes.map((prize, index) => (
                <div className="gd-prize-card" key={`${prize.label}-${index}`}>
                  <img src={withImgParams(prize.img, 'auto=format&fit=crop&w=500&q=75')} alt={prize.label} />
                  <p>{prize.label}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ===== ABOUT + TERMS ===== */}

        <div className="gd-about-terms">

          {(description || features.length > 0) && (
            <div className="gd-panel">
              <h2>ABOUT THIS BIKE</h2>
              {description && <p className="gd-desc-text">{description}</p>}

              {features.length > 0 && (
                <div className="gd-check-grid">
                  {features.map((feature, index) => (
                    <div className="gd-check-item" key={`${feature}-${index}`}>
                      <i className="fa-solid fa-circle-check"></i> {feature}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="gd-panel">
            <h2>TERMS &amp; CONDITIONS</h2>

            {TERMS.map((term, index) => (
              <div className={`gd-accordion-item ${openTerm === index ? 'gd-open' : ''}`} key={term.title}>
                <div className="gd-accordion-head" onClick={() => setOpenTerm(openTerm === index ? -1 : index)}>
                  <i className={`${term.icon} gd-left-icon`}></i>
                  <span>{term.title}</span>
                  <i className="fa-solid fa-chevron-down gd-chevron"></i>
                </div>
                <div className="gd-accordion-body">
                  <p>{term.body}</p>
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* ===== LIVE DRAW BANNER ===== */}

        <div className="gd-draw-banner">

          <div className="gd-draw-left">
            <div className="gd-trophy"><i className="fa-solid fa-trophy"></i></div>
            <div>
              <div className="gd-tag">WINNER ANNOUNCEMENT</div>
              <h2>LIVE DRAW &amp; ANNOUNCEMENT</h2>
              <p>The lucky winner will be selected in a live draw and announced on YouTube, Instagram &amp; Taurian Platform.</p>
            </div>
          </div>

          <div className="gd-draw-right">
            {drawDate && <div className="gd-date">{formatDate(drawDate).toUpperCase()}</div>}
            {drawDate && formatTime(drawDate) && <div className="gd-time">{formatTime(drawDate)}</div>}
            <div className="gd-social-icons">
              <a href="#" aria-label="YouTube"><i className="fa-brands fa-youtube"></i></a>
              <a href="#" aria-label="Instagram"><i className="fa-brands fa-instagram"></i></a>
              <a href="#" aria-label="Taurian" className="gd-social-brand">T</a>
            </div>
          </div>

        </div>

        {/* ===== RELATED GIVEAWAYS ===== */}

        {relatedItems.length > 0 && (
          <>
            <div className="gd-section-header">
              <i className="fa-solid fa-bars"></i>
              <h2>RELATED GIVEAWAYS</h2>
              <div className="gd-spacer"></div>
              <div className="gd-nav-arrows">
                <button><i className="fa-solid fa-chevron-left"></i></button>
                <button><i className="fa-solid fa-chevron-right"></i></button>
              </div>
            </div>

            <div className="gd-related-grid">
              {relatedItems.map((item, index) => {
                const card = (
                  <>
                    <div className="gd-rimg">
                      <span className={`gd-status-tag gd-${item.status}`}>{item.status === 'live' ? 'LIVE' : 'UPCOMING'}</span>
                      <img src={withImgParams(item.img, 'auto=format&fit=crop&w=600&q=75')} alt={item.title} />
                    </div>
                    <div className="gd-rcard-body">
                      <h3>{item.title}</h3>
                    </div>
                  </>
                );
                return item.id ? (
                  <Link className="gd-rcard" key={item.id} to={relatedHref(item.id)}>{card}</Link>
                ) : (
                  <div className="gd-rcard" key={`${item.title}-${index}`}>{card}</div>
                );
              })}
            </div>
          </>
        )}

        <footer className="gd-footer">© 2026 Taurian. All rights reserved.</footer>

      </div>
    </div>
  );
};

export default GiveawayDetailsStatic;
